// ABOUTME: React Query hook exposing the blab-auth session status, token claims and admin flag
// ABOUTME: Takes the BrowserAuth and skip flag as arguments so tests inject fakes; local dev is always the admin

import type { BrowserAuth } from '@boundarylab/auth/browser';
import { useQuery } from '@tanstack/react-query';
import { decodeClaims, auth as defaultAuth, SKIP_AUTH, type TokenClaims } from '../lib/auth-client';

export type AuthStatus = 'loading' | 'signed_in' | 'signed_out' | 'not_provisioned' | 'unavailable';

export interface AuthState {
  status: AuthStatus;
  claims: TokenClaims | null;
  isAdmin: boolean;
}

const LOCAL_CLAIMS: TokenClaims = { uid: 'local-user', email: 'local@localhost', roles: ['admin'] };

export function useAuth(auth: BrowserAuth = defaultAuth, skipAuth = SKIP_AUTH): AuthState {
  const { data, isError } = useQuery({
    queryKey: ['auth-session'],
    queryFn: () => auth.session(),
    staleTime: 60_000,
    enabled: !skipAuth,
  });

  if (skipAuth) return { status: 'signed_in', claims: LOCAL_CLAIMS, isAdmin: true };
  if (isError) return { status: 'unavailable', claims: null, isAdmin: false };
  if (!data) return { status: 'loading', claims: null, isAdmin: false };

  const claims = data.status === 'signed_in' ? decodeClaims(data.token) : null;
  return { status: data.status, claims, isAdmin: claims?.roles.includes('admin') ?? false };
}
